// Item-level coupons (Android QV-4738, feat/coupon-v2) — pure helpers, no React/Redux/IO.
//
// Coupon `type`: "0"/null/"" = order-level, "1" = universal item coupon (ALWAYS percentage),
// "2" = specific item coupon (valid only for the products/variants in its `items` JSON map).
// Coupon `flag` (backend convention): "0" = percentage, "1" = amount. Universal item coupons are
// treated as percentage REGARDLESS of flag (Android: web-created coupons had a flag mismatch).
//
// MONEY MODEL (the linchpin — mirrors InStorePaymentFragment.recomputeLineDiscount):
//   manual discount and the item coupon are stored SEPARATELY on the line
//   (manualDiscountAmt/manualDiscountPercent vs couponDiscount*); the coupon $ is derived on the
//   post-manual-discount price ("discount-first, always"), a % coupon is capped at the coupon's
//   Maximum Discount Amount (per unit; 0 = no cap), and the COMBINED per-unit total is folded into
//   `orderDiscount` (with orderDiscountPercent forced 0) — which the existing subtotal/tax/penny
//   engine reads unchanged. On save, discount_amt goes out MANUAL-only and the coupon goes in
//   item_coupon_json (see itemSession.js).

import { roundHalfUp, toNum } from "./money";

const S = (v) => (v == null ? "" : String(v));

/**
 * QV Altria — re-derive each restored line's `couponAltriaFunded` from the local coupons table.
 *
 * Byte-faithful to Android `InStoreRestoreViewModel.reStoreData`: the flag is NEVER carried by the
 * saved cart (Android's `item_coupon_json` doesn't hold it), so a restored cart reads it back from
 * the coupons DB by coupon NAME — `couponDealList.firstOrNull { it.name == model.couponDiscountName }
 * ?.altriaFundedDiscount == "1"` — and fails safe to "0" when the coupon was deleted, renamed or had
 * its Altria switch turned off since the sale was held. The lookup is built ONCE, outside the line
 * loop (Android does the same to avoid N DB hits).
 *
 * Consequence, and the reason the web must not simply trust its own snapshot: the restored flag is
 * the coupon's CURRENT value, not the value at hold time.
 *
 * The Altria LFP promotion code (`couponPromotionCode`, AGDC field 29) is re-derived the same way
 * and for the same reason: it belongs to the funded flag, so a coupon that is no longer funded
 * restores with no code.
 *
 * @param {Array} lines    restored cart lines (not mutated)
 * @param {Array} coupons  local coupon rows (IDB `coupons`, raw Go-bundle shape)
 * @returns {Array} lines with `couponAltriaFunded` ("1"/"0") and `couponPromotionCode` re-derived
 */
export function deriveRestoredAltriaFlags(lines, coupons) {
  if (!Array.isArray(lines)) return lines;
  const byName = new Map();
  for (const c of coupons || []) {
    const nm = S(c && c.name);
    if (nm === "" || byName.has(nm)) continue; // firstOrNull: the first row for a name wins
    byName.set(nm, {
      funded: S(c.altria_funded_discount ?? c.altriaFundedDiscount) === "1",
      promotionCode: S(c.promotion_code).trim(),
    });
  }
  return lines.map((l) => {
    const name = S(l && l.couponDiscountName);
    const row = name !== "" ? byName.get(name) : undefined;
    const funded = row?.funded === true;
    return {
      ...l,
      couponAltriaFunded: funded ? "1" : "0",
      couponPromotionCode: funded ? row.promotionCode : "",
    };
  });
}

/** yyyy-MM-dd for `now` (local) — coupon dates compare correctly as strings. */
export function todayYmd(now = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/** Android isCouponValidToday: blank bounds are open; string compare on yyyy-MM-dd. */
export function isCouponValidToday(start, end, now = new Date()) {
  const today = todayYmd(now);
  const afterStart = !S(start).trim() || today >= S(start).slice(0, 10);
  const beforeEnd = !S(end).trim() || today <= S(end).slice(0, 10);
  return afterStart && beforeEnd;
}

/** Item-level coupon? (type "1" universal / "2" specific) */
export const isItemLevelCoupon = (c) => c && (S(c.type) === "1" || S(c.type) === "2");
/** Order-level coupon? (legacy null/"" or explicit "0") */
export const isOrderLevelCoupon = (c) => {
  const t = S(c && c.type);
  return t === "" || t === "0";
};

/** Android cart-level "Coupons" dialog: order-level only, valid for today. is_disabled is ONLINE-only — NOT checked. */
export function filterOrderLevelCoupons(list, now = new Date()) {
  return (list || []).filter((c) => c && isOrderLevelCoupon(c) && isCouponValidToday(c.date_valid ?? c.dateValid, c.date_expire ?? c.dateExpire, now));
}

/** Android ItemCouponDialog: item-level only (type 1/2), valid for today. Enable/disable intentionally ignored. */
export function filterItemLevelCoupons(list, now = new Date()) {
  return (list || []).filter((c) => c && isItemLevelCoupon(c) && isCouponValidToday(c.date_valid ?? c.dateValid, c.date_expire ?? c.dateExpire, now));
}

/**
 * Specific coupon (type "2") item check — Android applyItemCoupon step 1. `items` is a JSON map
 * {productId: [variantIds]}; a variant line matches when its variantId is a KEY or in any value
 * list; a plain line matches on its productId the same two ways (defensive both ways, like Android).
 */
export function couponAppliesToLine(coupon, line) {
  let itemsMap;
  try {
    itemsMap = JSON.parse(coupon.items || "{}") || {};
  } catch {
    itemsMap = {};
  }
  if (Array.isArray(itemsMap)) {
    // tolerate a plain-array items payload: [id, id, ...]
    const ids = itemsMap.map((x) => S(x).trim());
    const key = line.isVariant ? S(line.variantId).trim() : S(line.orderProductId).trim();
    return ids.includes(key);
  }
  const keys = Object.keys(itemsMap).map((k) => S(k).trim());
  const flat = [];
  for (const v of Object.values(itemsMap)) if (Array.isArray(v)) for (const x of v) flat.push(S(x).trim());
  const key = line.isVariant ? S(line.variantId).trim() : S(line.orderProductId).trim();
  return keys.includes(key) || flat.includes(key);
}

/** Is the coupon a percentage for THIS apply? Universal (type 1) is ALWAYS percentage; specific goes by flag ("1"=amount, else %). */
export function itemCouponIsPercent(coupon, isSpecific) {
  return isSpecific ? S(coupon.flag) !== "1" : true;
}

/** Per-unit price the discounts work from (override wins over catalog). */
const perUnitOf = (line) => (toNum(line.orderUnitNewPrice) > 0 ? toNum(line.orderUnitNewPrice) : toNum(line.orderUnitPrice));

/** Per-unit manual discount $ — UNROUNDED (Android applyItemCoupon validation path). */
export function manualDiscountPerUnit(line) {
  const perUnit = perUnitOf(line);
  return toNum(line.manualDiscountPercent) > 0 ? (perUnit * toNum(line.manualDiscountPercent)) / 100 : toNum(line.manualDiscountAmt);
}

/**
 * Compute the per-unit coupon $ for validation — Android applyItemCoupon step 3 (UNROUNDED):
 * reduced = max(perUnit − manual, 0); % → min(reduced×val/100, maxDiscount when > 0); $ → value.
 * Returns { couponAmt, reducedPrice } — caller blocks when couponAmt > reducedPrice.
 */
export function computeItemCouponForApply(line, { isPercent, value, maxDiscount }) {
  const reducedPrice = Math.max(perUnitOf(line) - manualDiscountPerUnit(line), 0);
  const raw = isPercent ? (reducedPrice * toNum(value)) / 100 : toNum(value);
  const cap = isPercent ? toNum(maxDiscount) : 0;
  const couponAmt = cap > 0 ? Math.min(raw, cap) : raw;
  return { couponAmt, reducedPrice };
}

/**
 * Android recomputeLineDiscount — derive the line's coupon $ on the post-manual price and fold
 * manual + coupon into the combined per-unit `orderDiscount` the engine reads. MUTATES the line.
 * Call whenever the manual discount or the coupon on a line changes.
 */
export function recomputeLineDiscount(line) {
  const perUnit = perUnitOf(line);
  const manualPct = toNum(line.manualDiscountPercent);
  // % manual is rounded half-up here (Android roundOffDecimal); $ manual passes through raw.
  const manualAmt = manualPct > 0 ? roundHalfUp((perUnit * manualPct) / 100) : toNum(line.manualDiscountAmt);
  const reduced = Math.max(perUnit - manualAmt, 0);

  if (S(line.couponDiscountName) !== "") {
    let coupon;
    if (line.couponDiscountType === "percentage") {
      const raw = (reduced * toNum(line.couponDiscountValue)) / 100;
      const cap = toNum(line.couponMaxDiscount);
      coupon = cap > 0 ? Math.min(raw, cap) : raw; // % coupon honours the Maximum Discount cap
    } else {
      coupon = Math.min(toNum(line.couponDiscountValue), reduced); // $ coupon clamps to the reduced price
    }
    line.couponDiscountFinalAmt = roundHalfUp(coupon);
    // combined per-unit $ total drives the engine; % manual collapses to $ while a coupon is on
    line.orderDiscount = roundHalfUp(manualAmt + line.couponDiscountFinalAmt);
    line.orderDiscountPercent = 0;
  } else {
    // no item coupon: keep the manual discount exactly as entered so a % still scales
    line.couponDiscountFinalAmt = 0;
    if (manualPct > 0) {
      line.orderDiscountPercent = manualPct;
      line.orderDiscount = 0;
    } else {
      line.orderDiscount = toNum(line.manualDiscountAmt);
      line.orderDiscountPercent = 0;
    }
  }
}

/** Coupon-card label — Android CouponsAdapter: item coupons show just "X% OFF" / "$X OFF" (no min-order text). */
export function itemCouponLabel(coupon) {
  const isPercent = S(coupon.flag) === "0" || S(coupon.type) === "1";
  return isPercent ? `${S(coupon.discount)}% OFF` : `$${S(coupon.discount)} OFF`;
}

/**
 * exclude_coupons_store — per-line ORDER-coupon eligibility (Android addProductWithQuantity:
 * `categoryList.none { line's category ids contains it.id && it.excludeCouponsStore == "1" }`).
 * categoryCsv is the product's `cotegory` CSV; excludedIds the ids of register categories flagged
 * exclude-coupons. No categories (custom items) → eligible. Item-level coupons are NOT gated by this.
 */
export function computeCouponEligibility(categoryCsv, excludedIds) {
  const excl = excludedIds || [];
  if (!excl.length) return true;
  const ids = String(categoryCsv == null ? "" : categoryCsv)
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
  return !ids.some((id) => excl.includes(id));
}
